import { useAPI } from '@providers/useAPI';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AdminWorkspacesListResponseDto } from 'app/API';
import { useClampListPage } from 'app/hooks/useClampListPage';
import { useDebouncedValue } from 'app/hooks/useDebouncedValue';
import { useListQueryParams } from 'app/hooks/useListQueryParams';
import { adminWorkspacesKeys, TAdminWorkspacesListParams } from 'app/queries/adminWorkspaces/adminWorkspacesKeys';
import { createContext, ReactNode, useContext, useEffect, useMemo, useRef } from 'react';
import toast from 'react-hot-toast';
import { fetchAllWorkspacesForExport } from '../api/fetchAllWorkspacesForExport';
import { createFetchWorkspacesQueryFn } from '../api/fetchWorkspaces';
import { downloadWorkspacesCsv } from '../helpers/downloadWorkspacesCsv';
import { shouldNotifyWorkspacesError } from '../helpers/shouldNotifyWorkspacesError';

const SEARCH_DEBOUNCE_MS = 300;
const LIST_ORDER = { orderBy: 'name', orderType: 'asc' } as const;

type TState = {
  workspaces: AdminWorkspacesListResponseDto;
  isLoading: boolean;
  hasInitialError: boolean;
  retryWorkspaces: () => void;
  search: string;
  setSearch: (v: string) => void;
  page: number;
  pageSize: number;
  setPage: (page: number) => void;
  setPageSize: (v: number) => void;
  reloadPage: () => void;
  exportCSV: () => void;
  isExporting: boolean;
};

const PageContext = createContext<TState>({
  workspaces: { data: [], count: 0 },
  isLoading: false,
  hasInitialError: false,
  retryWorkspaces: () => null,
  search: '',
  setSearch: () => null,
  page: 1,
  pageSize: 25,
  setPage: () => null,
  setPageSize: () => null,
  reloadPage: () => null,
  exportCSV: () => null,
  isExporting: false,
});

type TProps = {
  children: ReactNode;
};

export const PageProvider = ({ children }: TProps) => {
  const { call } = useAPI();
  const queryClient = useQueryClient();
  const { page, pageSize, search, setSearch, setPage, setPageSize } = useListQueryParams({
    defaults: { pageSize: 25, ...LIST_ORDER },
    replaceSearch: true,
  });
  const debouncedSearch = useDebouncedValue(search, SEARCH_DEBOUNCE_MS);
  const isSearchSettling = search !== debouncedSearch;
  const lastNotifiedErrorUpdatedAt = useRef(0);
  const listParams = useMemo<TAdminWorkspacesListParams>(
    () => ({ page, pageSize, ...LIST_ORDER, search: debouncedSearch }),
    [debouncedSearch, page, pageSize],
  );

  const workspacesQuery = useQuery({
    queryKey: adminWorkspacesKeys.list(listParams),
    queryFn: createFetchWorkspacesQueryFn(call),
    placeholderData: keepPreviousData,
    enabled: !isSearchSettling,
  });

  useClampListPage({
    page,
    setPage,
    pageSize,
    count: workspacesQuery.data?.count,
    hasFreshData: workspacesQuery.isSuccess && !workspacesQuery.isPlaceholderData,
  });

  useEffect(() => {
    if (
      !workspacesQuery.isRefetchError ||
      workspacesQuery.data === undefined ||
      workspacesQuery.errorUpdatedAt === lastNotifiedErrorUpdatedAt.current
    ) {
      return;
    }
    lastNotifiedErrorUpdatedAt.current = workspacesQuery.errorUpdatedAt;
    if (shouldNotifyWorkspacesError(workspacesQuery.error)) {
      toast.error('Unable to refresh workspaces. Showing previously loaded data.');
    }
  }, [workspacesQuery.data, workspacesQuery.error, workspacesQuery.errorUpdatedAt, workspacesQuery.isRefetchError]);

  const exportMutation = useMutation({
    mutationFn: () => fetchAllWorkspacesForExport(call, { search, ...LIST_ORDER }),
    onSuccess: ({ data }) => {
      void downloadWorkspacesCsv(data);
    },
    onError: (error) => {
      if (shouldNotifyWorkspacesError(error)) {
        toast.error('Unable to export workspaces.');
      }
    },
  });

  const workspaces = workspacesQuery.data ?? { data: [], count: 0 };
  const isLoading = workspacesQuery.isFetching || isSearchSettling;
  const hasInitialError = workspacesQuery.isError && workspacesQuery.data === undefined;

  const retryWorkspaces = () => {
    void workspacesQuery.refetch();
  };

  const reloadPage = () => {
    void queryClient.invalidateQueries({ queryKey: adminWorkspacesKeys.lists() });
  };

  const exportCSV = () => {
    if (!exportMutation.isPending) {
      exportMutation.mutate();
    }
  };

  return (
    <PageContext.Provider
      value={{
        workspaces,
        isLoading,
        hasInitialError,
        retryWorkspaces,
        search,
        setSearch,
        page,
        setPage,
        pageSize,
        setPageSize,
        reloadPage,
        exportCSV,
        isExporting: exportMutation.isPending,
      }}
    >
      {children}
    </PageContext.Provider>
  );
};

export const usePageContext = () => useContext(PageContext);
